import React from "react";
import { PencilIcon } from "lucide-react-native";
import { useFormContext } from "react-hook-form";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnText } from "@/components/VnText/VnText";

type VnExpandableCardPropsType = {
  title: string;
  subtitle?: string;
  subtitleComponent?: React.ReactNode;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  children: React.ReactNode;
  shouldShowEditButton: boolean;
};

const CardHeader = ({
  title,
  shouldShowEditButton,
  isExpanded,
  onToggleExpand,
  onCancel,
  onSave,
  isLoading,
}: {
  title: string;
  shouldShowEditButton: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onCancel: () => void;
  onSave: () => void;
  isLoading: boolean;
}) => {
  const { formState } = useFormContext();
  return (
    <VnBox className="flex min-h-9 flex-row items-center justify-between">
      <VnText variant="lgMedium">{title}</VnText>
      {!isExpanded && (
        <VnButton
          size={shouldShowEditButton ? "icon" : "sm"}
          variant={shouldShowEditButton ? "ghost" : "default"}
          IconComponent={shouldShowEditButton ? PencilIcon : undefined}
          onPress={onToggleExpand}>
          {!shouldShowEditButton && <VnText i18nKey="common:add" />}
        </VnButton>
      )}
      {isExpanded && (
        <VnBox className="flex flex-row gap-2">
          <VnButton
            variant="outline"
            size="sm"
            onPress={onCancel}
            className="flex-1"
            i18nKey="common:cancel"
          />
          <VnButton
            variant="default"
            size="sm"
            disabled={!formState.isDirty || isLoading}
            onPress={onSave}
            className="flex-1"
            isLoading={isLoading}
            i18nKey="common:save"
          />
        </VnBox>
      )}
    </VnBox>
  );
};

export const VnExpandableCard: React.FC<VnExpandableCardPropsType> = ({
  title,
  subtitle,
  subtitleComponent,
  isExpanded,
  onToggleExpand,
  onSave,
  onCancel,
  isLoading = false,
  children,
  shouldShowEditButton,
}) => {
  return (
    <VnCard className="gap-1 rounded-md p-4">
      <CardHeader
        title={title}
        shouldShowEditButton={shouldShowEditButton}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onCancel={onCancel}
        onSave={onSave}
        isLoading={isLoading}
      />
      {!isExpanded && (
        <>
          {subtitle && (
            <VnText variant="lg" className="text-muted-foreground">
              {subtitle}
            </VnText>
          )}
          {subtitleComponent}
        </>
      )}
      {isExpanded && (
        <VnBox className="bg-secondary mt-2 gap-4 rounded-md">{children}</VnBox>
      )}
    </VnCard>
  );
};
